import { Event } from "../../base/common/events.js";
import { IHierarchyService } from "../../platform/hierarchy/common/hierarchy.js";
import { IResourceService } from "../../platform/resources/common/resources.js";
import { resolveHandleInGraphs } from "../../platform/webgl/common/objectHandle.js";
import {
    newRenderInfoCursor,
    readPassStats,
    readProgramInfo,
    readRendererInfo,
    RenderStatsAccumulator,
    type RenderInfoCursor,
} from "../../platform/webgl/common/renderStats.js";
import { countSceneTriangles } from "../../platform/webgl/common/sceneStats.js";
import type { IWebGLEngine } from "../../platform/webgl/common/webgl.js";

export interface AgentThreeEngine extends IWebGLEngine {
    observe(object: unknown): void;
}

/** Minimal Three.js engine for the headless product; observes host renderers but owns none. */
export function createAgentEngineShim(
    hierarchy: IHierarchyService | undefined,
    resources: IResourceService | null | undefined,
): AgentThreeEngine {
    const renderers = new Map<object, { lastRender: number; stats: RenderStatsAccumulator; cursor: RenderInfoCursor }>();
    let lastRenderer: object | null = null;
    let lastScene: object | null = null;
    let lastCamera: object | null = null;

    const observeRenderer = (renderer: object) => {
        if (renderers.has(renderer)) return;
        const state = { lastRender: 0, stats: new RenderStatsAccumulator(), cursor: newRenderInfoCursor() };
        renderers.set(renderer, state);

        const candidate = renderer as { render?: (...args: unknown[]) => unknown };
        const original = candidate.render;
        if (typeof original !== "function") return;
        candidate.render = function (scene: unknown, camera: unknown, ...rest: unknown[]) {
            const result = original.call(this, scene, camera, ...rest);
            state.lastRender = Date.now();
            lastRenderer = renderer;
            if (scene && typeof scene === "object") lastScene = scene;
            if (camera && typeof camera === "object") lastCamera = camera;
            state.stats.addPass(performance.now(), readPassStats(readRendererInfo(renderer), state.cursor));
            return result;
        };
    };

    return {
        name: "threejs",
        onDidChange: Event.None,
        observe(object: unknown) {
            if (!object || typeof object !== "object") return;
            const value = object as { isScene?: boolean; isCamera?: boolean; isWebGLRenderer?: boolean; isWebGPURenderer?: boolean };
            if (value.isScene) lastScene = object;
            if (value.isCamera) lastCamera = object;
            if (value.isWebGLRenderer || value.isWebGPURenderer) observeRenderer(object);
        },
        getScene: <TScene = unknown>() => lastScene as TScene | null,
        getScenes: <TScene = unknown>() => lastScene ? [lastScene as TScene] : [],
        getRenderer: <TRenderer = unknown>() => lastRenderer as TRenderer | null,
        getRenderers: () => [...renderers.keys()],
        getCamera: <TCamera = unknown>() => lastCamera as TCamera | null,
        getRenderStats(opts) {
            if (!lastRenderer) return null;
            return renderers.get(lastRenderer)?.stats.summarize(performance.now(), opts) ?? null;
        },
        getSceneTriangleCounts: () => countSceneTriangles(lastScene as never, () => false),
        getProgramInfo() {
            let count = 0;
            let bytes = 0;
            let hasBytes = false;
            for (const renderer of renderers.keys()) {
                const info = readProgramInfo(renderer);
                if (!info) continue;
                count += info.count;
                if (info.bytes !== undefined) {
                    bytes += info.bytes;
                    hasBytes = true;
                }
            }
            return count > 0 ? { count, ...(hasBytes ? { bytes } : {}) } : null;
        },
        describeRenderers(recentMs = 1000) {
            const now = Date.now();
            return [...renderers].map(([renderer, state]) => ({
                type: renderer.constructor?.name || "ThreeRenderer",
                active: state.lastRender > 0 && now - state.lastRender <= recentMs,
            }));
        },
        findObjectById(id: string): unknown | null {
            const roots = (hierarchy?.roots ?? []).map(node => node.owner as { id?: unknown; uuid?: unknown; children?: readonly unknown[] });
            const found = resolveHandleInGraphs(roots, resources ?? null, id);
            if (found) return found.object;
            return hierarchy?.findById(id)?.owner ?? null;
        },
    };
}
